import React, { useEffect, useState } from 'react';
import { Star, Pin, Eye, UserCheck, DollarSign, TrendingUp, Clock, Activity, Zap, ChevronRight, ArrowUp, ArrowDown, Play, Pause, Trash2, Calendar, CreditCard, Info, CheckCircle2, LayoutList, ExternalLink, Database, Server, History, Receipt, AlertCircle, RefreshCw, Mail, Settings, X, BarChart3, MousePointer, Sparkles, SplitSquareHorizontal, Edit, Target, Timer, ArrowRight, AlertTriangle, Layers } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import { Button } from '../ui/Button';
interface FeaturedAstrologersManagerProps {
  astrologers: any[];
  setDetailPanel: (panel: any) => void;
  success: (msg: string) => void;
  setShowCreatePin: (type: 'astrologer' | null) => void;
}
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    maximumFractionDigits: 0
  }).format(amount);
};
// Mock Audit Logs
const initialLogs = [{
  id: 'LOG-1',
  time: '10:42:15',
  type: 'auto_swap',
  message: 'Авто-замена: FA-01 завершен. Активирован FA-03.',
  status: 'success'
}, {
  id: 'LOG-2',
  time: '10:41:02',
  type: 'payment',
  message: 'Оплата подтверждена для FA-02. Статус изменен на "Активен".',
  status: 'success'
}, {
  id: 'LOG-3',
  time: '10:30:00',
  type: 'notification',
  message: 'Отправлено 24 SMS-уведомлений о старте кампаний.',
  status: 'info'
}];
export function FeaturedAstrologersManager({
  astrologers: initialAstrologers,
  setDetailPanel,
  success,
  setShowCreatePin
}: FeaturedAstrologersManagerProps) {
  const [campaigns, setCampaigns] = useState(initialAstrologers);
  const [activeModal, setActiveModal] = useState<'logs' | 'payment' | 'timing' | 'analytics' | 'ab_test' | null>(null);
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null);
  const [logs, setLogs] = useState(initialLogs);
  // Sync with props
  useEffect(() => {
    setCampaigns(initialAstrologers);
  }, [initialAstrologers]);
  const addLog = (type: string, message: string, status: string) => {
    setLogs(prev => [{
      id: `LOG-${Date.now()}`,
      time: new Date().toLocaleTimeString('ru-RU'),
      type,
      message,
      status
    }, ...prev].slice(0, 50));
  };
  const handleAction = (action: string, campaign: any, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    switch (action) {
      case 'view_profile':
        window.open(`/astrologer/${campaign.id}`, '_blank');
        break;
      case 'view_admin':
        setDetailPanel({
          type: 'featured-astrologer',
          title: campaign.astrologer,
          data: campaign
        });
        break;
      case 'view_advertiser':
        setDetailPanel({
          type: 'user-profile',
          title: campaign.advertiser,
          data: {
            name: campaign.advertiser,
            type: campaign.advertiserType
          }
        });
        break;
      case 'view_slot':
        setDetailPanel({
          type: 'slot-detail',
          title: campaign.slot,
          data: {
            name: campaign.slot
          }
        });
        break;
      case 'analytics':
        setSelectedCampaign(campaign);
        setActiveModal('analytics');
        break;
      case 'payment':
        setSelectedCampaign(campaign);
        setActiveModal('payment');
        break;
      case 'timing':
        setSelectedCampaign(campaign);
        setActiveModal('timing');
        break;
      case 'ab_test':
        setSelectedCampaign(campaign);
        setActiveModal('ab_test');
        break;
      case 'activate':
        if (campaign.paymentStatus !== 'paid') {
          addLog('error', `Отказ активации ${campaign.id}: Требуется оплата`, 'error');
          alert('Невозможно активировать: требуется оплата');
          return;
        }
        success(`Кампания ${campaign.id} активирована`);
        addLog('activation', `Кампания ${campaign.id} успешно активирована`, 'success');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'active'
        } : c));
        break;
      case 'pause':
        success(`Кампания ${campaign.id} приостановлена`);
        addLog('pause', `Кампания ${campaign.id} поставлена на паузу`, 'warning');
        setCampaigns(prev => prev.map(c => c.id === campaign.id ? {
          ...c,
          status: 'paused'
        } : c));
        break;
      case 'fastTrack':
        success(`Fast Track включен для ${campaign.id}`);
        addLog('fast_track', `Fast Track включен для ${campaign.id}`, 'info');
        break;
      case 'delete':
        if (confirm(`Удалить кампанию ${campaign.id}?`)) {
          success(`Кампания ${campaign.id} удалена`);
          setCampaigns(prev => prev.filter(c => c.id !== campaign.id));
        }
        break;
    }
  };
  // --- METRICS CALCULATION ---
  const totalImpressions = campaigns.reduce((acc, c) => acc + (c.impressions || 0), 0);
  const totalClicks = campaigns.reduce((acc, c) => acc + (c.clicks || 0), 0);
  const totalBookings = campaigns.reduce((acc, c) => acc + (c.bookings || 0), 0);
  const totalRevenue = campaigns.reduce((acc, c) => acc + (c.status === 'active' ? c.price : 0), 0);
  const avgConsultationPrice = 5000; // Mock avg check for consultation
  const avgCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100).toFixed(2) : '0.00';
  const avgCr = totalClicks > 0 ? (totalBookings / totalClicks * 100).toFixed(2) : '0.00';
  const avgCpa = totalBookings > 0 ? Math.round(totalRevenue / totalBookings) : 0;
  const avgRpm = totalImpressions > 0 ? Math.round(totalRevenue / totalImpressions * 1000) : 0;
  const renderKpis = () => <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-6" data-id="element-11635">
      <Card padding="sm" hoverable className="bg-indigo-50 border-indigo-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11636">
        <div className="text-[10px] text-indigo-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11637">
          <Pin className="w-3 h-3" data-id="element-11638" /> Активные
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11639">
          {campaigns.filter(c => c.status === 'active').length}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-blue-50 border-blue-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11640">
        <div className="text-[10px] text-blue-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11641">
          <Eye className="w-3 h-3" data-id="element-11642" /> Показы
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11643">
          {totalImpressions.toLocaleString()}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-sky-50 border-sky-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11644">
        <div className="text-[10px] text-sky-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11645">
          <MousePointer className="w-3 h-3" data-id="element-11646" /> Клики
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11647">
          {totalClicks.toLocaleString()}
        </div>
        <div className="text-[10px] text-sky-600 mt-1" data-id="element-11648">CTR {avgCtr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-emerald-50 border-emerald-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11649">
        <div className="text-[10px] text-emerald-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11650">
          <UserCheck className="w-3 h-3" data-id="element-11651" /> Записи
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11652">
          {totalBookings.toLocaleString()}
        </div>
        <div className="text-[10px] text-emerald-600 mt-1" data-id="element-11653">CR {avgCr}%</div>
      </Card>
      <Card padding="sm" hoverable className="bg-green-50 border-green-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11654">
        <div className="text-[10px] text-green-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11655">
          <DollarSign className="w-3 h-3" data-id="element-11656" /> Доход (Ads)
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11657">
          {formatCurrency(totalRevenue)}
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-amber-50 border-amber-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11658">
        <div className="text-[10px] text-amber-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11659">
          <Target className="w-3 h-3" data-id="element-11660" /> CPA
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11661">
          {formatCurrency(avgCpa)}
        </div>
        <div className="text-[10px] text-amber-600 mt-1" data-id="element-11662">Цена за запись</div>
      </Card>
      <Card padding="sm" hoverable className="bg-purple-50 border-purple-100 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11663">
        <div className="text-[10px] text-purple-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11664">
          <Activity className="w-3 h-3" data-id="element-11665" /> RPM
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11666">
          {formatCurrency(avgRpm)}
        </div>
        <div className="text-[10px] text-purple-600 mt-1" data-id="element-11667">
          Доход на 1000 пок.
        </div>
      </Card>
      <Card padding="sm" hoverable className="bg-slate-50 border-slate-200 cursor-pointer" onClick={() => setActiveModal('analytics')} data-id="element-11668">
        <div className="text-[10px] text-slate-600 font-bold uppercase mb-1 flex items-center gap-1" data-id="element-11669">
          <CreditCard className="w-3 h-3" data-id="element-11670" /> Ср. чек конс.
        </div>
        <div className="text-xl font-bold text-slate-900" data-id="element-11671">
          {formatCurrency(avgConsultationPrice)}
        </div>
      </Card>
    </div>;
  const renderAiRecommendations = () => <Card padding="sm" className="mb-6 border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50" data-id="element-11672">
      <div className="flex items-start gap-4" data-id="element-11673">
        <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0" data-id="element-11674">
          <Sparkles className="w-5 h-5 text-indigo-600" data-id="element-11675" />
        </div>
        <div className="flex-1" data-id="element-11676">
          <h3 className="text-sm font-bold text-indigo-900 mb-2" data-id="element-11677">
            AI Рекомендации для платформы
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-11678">
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11679">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11680">
                Оптимизация профиля
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11681">
                Астролог FA-02 имеет низкий CR (3.4%). Рекомендуется обновить
                главное фото и добавить видео-визитку для повышения доверия.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11682">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11683">
                Повышение позиции
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11684">
                Слот "Каталог астрологов - TOP 3" свободен со следующей недели.
                Предложите текущим рекламодателям апгрейд со скидкой 10%.
              </div>
            </div>
            <div className="bg-white/60 p-3 rounded-lg border border-indigo-100" data-id="element-11685">
              <div className="text-xs font-bold text-slate-800 mb-1" data-id="element-11686">
                A/B тест заголовков
              </div>
              <div className="text-[10px] text-slate-600" data-id="element-11687">
                Запустите автоматическое A/B тестирование заголовков профилей в
                каталоге для увеличения общего CTR на 15-20%.
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>;
  const renderTable = () => <Card padding="none" className="rounded-xl shadow-sm border-slate-200 overflow-hidden" data-id="element-11688">
      <div className="p-4 border-b border-slate-200 bg-white flex justify-between items-center" data-id="element-11689">
        <div className="flex items-center gap-3" data-id="element-11690">
          <div className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center" data-id="element-11691">
            <Star className="w-4 h-4 text-amber-500" data-id="element-11692" />
          </div>
          <div data-id="element-11693">
            <h3 className="text-sm font-bold text-slate-900" data-id="element-11694">
              Управление продвижением астрологов
            </h3>
            <p className="text-[10px] text-slate-500 mt-0.5" data-id="element-11695">
              Ads Manager для профилей специалистов
            </p>
          </div>
        </div>
        <div className="flex gap-2" data-id="element-11696">
          <Button variant="outline" size="sm" icon={<History className="w-4 h-4" data-id="element-11698" />} onClick={() => setActiveModal('logs')} data-id="element-11697">
            Audit Logs
          </Button>
          <Button variant="primary" size="sm" icon={<Pin className="w-4 h-4" data-id="element-11700" />} onClick={() => setShowCreatePin('astrologer')} data-id="element-11699">
            Создать кампанию
          </Button>
        </div>
      </div>

      <div className="overflow-x-auto" data-id="element-11701">
        <table className="w-full text-left whitespace-nowrap" data-id="element-11702">
          <thead className="bg-slate-50 text-slate-500 border-b border-slate-200 text-[10px] uppercase tracking-wider" data-id="element-11703">
            <tr data-id="element-11704">
              <th className="px-4 py-3 font-medium" data-id="element-11705">Кампания / Астролог</th>
              <th className="px-4 py-3 font-medium" data-id="element-11706">Рекламодатель</th>
              <th className="px-4 py-3 font-medium" data-id="element-11707">Слот / Позиция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11708">Таймер / Период</th>
              <th className="px-4 py-3 font-medium" data-id="element-11709">Коммерция</th>
              <th className="px-4 py-3 font-medium" data-id="element-11710">
                Воронка (Показы → Клики → Записи)
              </th>
              <th className="px-4 py-3 font-medium" data-id="element-11711">Статус / Очередь</th>
              <th className="px-4 py-3 text-right font-medium" data-id="element-11712">Действия</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-11713">
            {campaigns.map(c => {
            const ctr = c.impressions > 0 ? (c.clicks / c.impressions * 100).toFixed(1) : '0.0';
            const cr = c.clicks > 0 ? (c.bookings / c.clicks * 100).toFixed(1) : '0.0';
            const isPaid = c.paymentStatus === 'paid';
            // Timer logic simulation
            let timerColor = 'text-slate-500';
            let timerText = 'Ожидание';
            if (c.status === 'active') {
              timerColor = 'text-emerald-600';
              timerText = 'Осталось 14 дн.';
            } else if (c.status === 'queued') {
              timerColor = 'text-amber-600';
              timerText = 'Старт через 4 дн.';
            }
            return <tr key={c.id} className="hover:bg-slate-50 transition-colors group bg-white" data-id="element-11714">
                  {/* Astrologer Info */}
                  <td className="px-4 py-3" data-id="element-11715">
                    <div className="flex items-center gap-2 mb-1" data-id="element-11716">
                      <span className="text-[10px] font-mono font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:underline" onClick={() => handleAction('view_admin', c)} data-id="element-11717">
                        {c.id}
                      </span>
                      {c.creatives?.length > 1 && <Badge variant="pro" size="sm" className="text-[9px] py-0 cursor-pointer" onClick={() => handleAction('ab_test', c)} data-id="element-11718">
                          <SplitSquareHorizontal className="w-2.5 h-2.5 mr-0.5" data-id="element-11719" />{' '}
                          A/B Test
                        </Badge>}
                    </div>
                    <div className="text-sm font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1" onClick={() => handleAction('view_profile', c)} data-id="element-11720">
                      {c.astrologer}{' '}
                      <ExternalLink className="w-3 h-3 opacity-50" data-id="element-11721" />
                    </div>
                  </td>

                  {/* Advertiser */}
                  <td className="px-4 py-3" data-id="element-11722">
                    <div className="text-xs font-medium text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5" onClick={() => handleAction('view_advertiser', c)} data-id="element-11723">
                      <UserCheck className="w-3.5 h-3.5 text-slate-400" data-id="element-11724" />
                      {c.advertiser}
                    </div>
                    <div className="text-[10px] text-slate-500 mt-0.5" data-id="element-11725">
                      {c.advertiserType === 'astrologer' ? 'Сам астролог' : 'Продюсер / Агентство'}
                    </div>
                  </td>

                  {/* Slot & Position */}
                  <td className="px-4 py-3" data-id="element-11726">
                    <div className="text-xs font-bold text-slate-900 cursor-pointer hover:text-indigo-600 flex items-center gap-1.5 mb-1" onClick={() => handleAction('view_slot', c)} data-id="element-11727">
                      <LayoutList className="w-3.5 h-3.5 text-slate-400" data-id="element-11728" />
                      {c.slot}
                    </div>
                    <Badge variant="neutral" size="sm" className="text-[10px]" data-id="element-11729">
                      {c.position}
                    </Badge>
                  </td>

                  {/* Timer & Period */}
                  <td className="px-4 py-3" data-id="element-11730">
                    <div className={`text-xs font-bold flex items-center gap-1.5 mb-1 cursor-pointer hover:underline ${timerColor}`} onClick={() => handleAction('timing', c)} data-id="element-11731">
                      <Clock className="w-3.5 h-3.5" data-id="element-11732" />
                      {timerText}
                    </div>
                    <div className="text-[10px] text-slate-500 flex items-center gap-1" data-id="element-11733">
                      <Calendar className="w-3 h-3" data-id="element-11734" />
                      {c.periodStart} — {c.periodEnd}
                    </div>
                  </td>

                  {/* Commerce */}
                  <td className="px-4 py-3" data-id="element-11735">
                    <div className="text-sm font-bold text-slate-900 mb-1" data-id="element-11736">
                      {formatCurrency(c.price)}
                    </div>
                    <div className={`inline-flex items-center gap-1 text-[9px] font-bold px-1.5 py-0.5 rounded cursor-pointer transition-colors ${isPaid ? 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`} onClick={() => handleAction('payment', c)} data-id="element-11737">
                      <Receipt className="w-2.5 h-2.5" data-id="element-11738" />
                      {isPaid ? 'Оплачено' : 'Ожидает оплаты'}
                    </div>
                  </td>

                  {/* Funnel Metrics */}
                  <td className="px-4 py-3" data-id="element-11739">
                    <div className="flex items-center gap-4 cursor-pointer hover:bg-slate-50 p-1 rounded transition-colors" onClick={() => handleAction('analytics', c)} data-id="element-11740">
                      <div className="flex flex-col items-center w-16" data-id="element-11741">
                        <span className="text-xs font-bold text-slate-900" data-id="element-11742">
                          {c.impressions.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11743">
                          Показы
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11744" />
                      <div className="flex flex-col items-center w-16" data-id="element-11745">
                        <span className="text-xs font-bold text-blue-600" data-id="element-11746">
                          {c.clicks.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11747">
                          CTR {ctr}%
                        </span>
                      </div>
                      <ArrowRight className="w-3 h-3 text-slate-300" data-id="element-11748" />
                      <div className="flex flex-col items-center w-16" data-id="element-11749">
                        <span className="text-xs font-bold text-emerald-600" data-id="element-11750">
                          {c.bookings.toLocaleString()}
                        </span>
                        <span className="text-[9px] text-slate-500 uppercase" data-id="element-11751">
                          CR {cr}%
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Status & Queue */}
                  <td className="px-4 py-3" data-id="element-11752">
                    <div className="flex flex-col gap-1.5 items-start" data-id="element-11753">
                      <Badge variant={c.status === 'active' ? 'success' : c.status === 'queued' ? 'warning' : 'neutral'} size="sm" data-id="element-11754">
                        {c.status === 'active' ? 'Транслируется' : c.status === 'queued' ? 'В очереди' : 'Пауза'}
                      </Badge>

                      {c.queuePosition && <div className="text-[10px] text-indigo-600 font-medium bg-indigo-50 px-1.5 py-0.5 rounded cursor-pointer hover:bg-indigo-100 flex items-center gap-1" onClick={() => handleAction('timing', c)} data-id="element-11755">
                          <LayoutList className="w-3 h-3" data-id="element-11756" />
                          Очередь: #{c.queuePosition}
                        </div>}
                    </div>
                  </td>

                  {/* Actions */}
                  <td className="px-4 py-3 text-right" data-id="element-11757">
                    <div className="flex items-center justify-end gap-1" data-id="element-11758">
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Аналитика" onClick={() => handleAction('analytics', c)} data-id="element-11759">
                        <BarChart3 className="w-3.5 h-3.5" data-id="element-11760" />
                      </Button>
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-slate-400 hover:text-indigo-600" title="Редактировать" onClick={() => handleAction('view_admin', c)} data-id="element-11761">
                        <Edit className="w-3.5 h-3.5" data-id="element-11762" />
                      </Button>
                      {c.status === 'active' ? <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-amber-500 hover:bg-amber-50" title="Пауза" onClick={() => handleAction('pause', c)} data-id="element-11763">
                          <Pause className="w-3.5 h-3.5" data-id="element-11764" />
                        </Button> : <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-emerald-500 hover:bg-emerald-50" title="Активировать" onClick={e => handleAction('activate', c, e)} data-id="element-11765">
                          <Play className="w-3.5 h-3.5 fill-current" data-id="element-11766" />
                        </Button>}
                      <Button variant="ghost" size="sm" className="w-7 h-7 p-0 text-rose-400 hover:text-rose-600 hover:bg-rose-50" title="Удалить" onClick={() => handleAction('delete', c)} data-id="element-11767">
                        <Trash2 className="w-3.5 h-3.5" data-id="element-11768" />
                      </Button>
                    </div>
                  </td>
                </tr>;
          })}
          </tbody>
        </table>
      </div>
    </Card>;
  const renderModals = () => {
    if (!activeModal) return null;
    return <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4" onClick={e => {
      if (e.target === e.currentTarget) setActiveModal(null);
    }} data-id="element-11769">
        {/* LOGS MODAL */}
        {activeModal === 'logs' && <Card className="w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11770">
            <div className="p-4 border-b border-slate-200 bg-slate-900 text-white flex justify-between items-center shrink-0" data-id="element-11771">
              <div className="flex items-center gap-3" data-id="element-11772">
                <Server className="w-5 h-5 text-indigo-400" data-id="element-11773" />
                <div data-id="element-11774">
                  <h3 className="font-bold" data-id="element-11775">Audit Logs: Ads Manager</h3>
                  <p className="text-[10px] text-slate-400" data-id="element-11776">
                    Журнал событий рекламной платформы
                  </p>
                </div>
              </div>
              <Button variant="ghost" size="sm" className="text-slate-400 hover:text-white" icon={<X className="w-5 h-5" data-id="element-11778" />} onClick={() => setActiveModal(null)} data-id="element-11777" />
            </div>
            <div className="p-0 overflow-y-auto bg-slate-950 flex-1 font-mono text-xs" data-id="element-11779">
              {logs.map(log => <div key={log.id} className="flex gap-4 p-3 border-b border-slate-800 hover:bg-slate-900/50" data-id="element-11780">
                  <div className="text-slate-500 shrink-0" data-id="element-11781">{log.time}</div>
                  <div className={`shrink-0 w-24 ${log.status === 'error' ? 'text-rose-400' : log.status === 'warning' ? 'text-amber-400' : log.status === 'success' ? 'text-emerald-400' : 'text-blue-400'}`} data-id="element-11782">
                    [{log.type.toUpperCase()}]
                  </div>
                  <div className="text-slate-300" data-id="element-11783">{log.message}</div>
                </div>)}
            </div>
          </Card>}

        {/* PAYMENT MODAL */}
        {activeModal === 'payment' && selectedCampaign && <Card className="w-full max-w-md shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11784">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11785">
              <div className="flex items-center gap-2" data-id="element-11786">
                <Receipt className="w-5 h-5 text-indigo-600" data-id="element-11787" />
                <h3 className="font-bold text-slate-900" data-id="element-11788">
                  Детали оплаты кампании
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11790" />} onClick={() => setActiveModal(null)} data-id="element-11789" />
            </div>
            <div className="p-6 space-y-6" data-id="element-11791">
              <div className="text-center" data-id="element-11792">
                <div className="text-sm text-slate-500 mb-1" data-id="element-11793">
                  Сумма к оплате
                </div>
                <div className="text-3xl font-bold text-slate-900" data-id="element-11794">
                  {formatCurrency(selectedCampaign.price || 0)}
                </div>
                <Badge variant={selectedCampaign.paymentStatus === 'paid' ? 'success' : 'warning'} className="mt-2" data-id="element-11795">
                  {selectedCampaign.paymentStatus === 'paid' ? 'Оплачено' : 'Ожидает оплаты'}
                </Badge>
              </div>

              <div className="bg-slate-50 rounded-xl p-4 space-y-3 text-sm" data-id="element-11796">
                <div className="flex justify-between" data-id="element-11797">
                  <span className="text-slate-500" data-id="element-11798">ID Кампании</span>
                  <span className="font-bold text-slate-900" data-id="element-11799">
                    {selectedCampaign.id}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11800">
                  <span className="text-slate-500" data-id="element-11801">Инвойс</span>
                  <span className="font-mono text-indigo-600" data-id="element-11802">
                    {selectedCampaign.invoiceId || 'INV-PENDING'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11803">
                  <span className="text-slate-500" data-id="element-11804">Транзакция</span>
                  <span className="font-mono text-slate-900" data-id="element-11805">
                    {selectedCampaign.paymentId || '—'}
                  </span>
                </div>
                <div className="flex justify-between" data-id="element-11806">
                  <span className="text-slate-500" data-id="element-11807">Рекламодатель</span>
                  <span className="font-medium text-slate-900" data-id="element-11808">
                    {selectedCampaign.advertiser}
                  </span>
                </div>
              </div>

              {selectedCampaign.paymentStatus !== 'paid' && <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3" data-id="element-11809">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" data-id="element-11810" />
                  <div className="text-sm text-amber-800" data-id="element-11811">
                    <strong data-id="element-11812">Блокировка активации:</strong> Кампания не будет
                    запущена до подтверждения оплаты.
                  </div>
                </div>}

              <div className="flex gap-3 pt-4 border-t border-slate-100" data-id="element-11813">
                <Button variant="outline" className="flex-1" onClick={() => setActiveModal(null)} data-id="element-11814">
                  Закрыть
                </Button>
                {selectedCampaign.paymentStatus !== 'paid' && <Button variant="primary" className="flex-1" onClick={() => {
              success('Оплата подтверждена');
              addLog('payment', `Ручное подтверждение оплаты для ${selectedCampaign.id}`, 'success');
              setCampaigns(prev => prev.map(c => c.id === selectedCampaign.id ? {
                ...c,
                paymentStatus: 'paid',
                paymentId: 'PAY-MANUAL-' + Date.now()
              } : c));
              setActiveModal(null);
            }} data-id="element-11815">
                    Подтвердить оплату
                  </Button>}
              </div>
            </div>
          </Card>}

        {/* TIMING MODAL */}
        {activeModal === 'timing' && selectedCampaign && <Card className="w-full max-w-sm shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11816">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11817">
              <div className="flex items-center gap-2" data-id="element-11818">
                <Clock className="w-5 h-5 text-indigo-600" data-id="element-11819" />
                <h3 className="font-bold text-slate-900" data-id="element-11820">Тайминг и Очередь</h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11822" />} onClick={() => setActiveModal(null)} data-id="element-11821" />
            </div>
            <div className="p-6 space-y-4 text-sm" data-id="element-11823">
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11824">
                <span className="text-slate-500" data-id="element-11825">Статус</span>
                <Badge variant={selectedCampaign.status === 'active' ? 'success' : 'warning'} data-id="element-11826">
                  {selectedCampaign.status === 'active' ? 'Транслируется' : 'В очереди'}
                </Badge>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11827">
                <span className="text-slate-500" data-id="element-11828">Период</span>
                <span className="font-medium text-slate-900" data-id="element-11829">
                  {selectedCampaign.periodStart} — {selectedCampaign.periodEnd}
                </span>
              </div>

              {selectedCampaign.status === 'queued' && <>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11830">
                    <span className="text-slate-500" data-id="element-11831">Позиция в очереди</span>
                    <span className="font-bold text-indigo-600" data-id="element-11832">
                      #{selectedCampaign.queuePosition}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11833">
                    <span className="text-slate-500" data-id="element-11834">Ожидаемый старт</span>
                    <span className="font-bold text-slate-900" data-id="element-11835">
                      {selectedCampaign.expectedStartAt || 'Неизвестно'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center py-2 border-b border-slate-100" data-id="element-11836">
                    <span className="text-slate-500" data-id="element-11837">Время ожидания</span>
                    <span className="font-medium text-amber-600" data-id="element-11838">
                      {selectedCampaign.waitTime || '—'}
                    </span>
                  </div>
                </>}

              <div className="mt-6 bg-indigo-50 p-4 rounded-xl text-xs text-indigo-800 flex gap-2" data-id="element-11839">
                <Info className="w-4 h-4 shrink-0 mt-0.5" data-id="element-11840" />
                <p data-id="element-11841">
                  Система автоматически активирует кампанию при освобождении
                  слота "{selectedCampaign.slot}" (onExpire event).
                </p>
              </div>
            </div>
          </Card>}

        {/* ANALYTICS MODAL */}
        {activeModal === 'analytics' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11842">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11843">
              <div className="flex items-center gap-2" data-id="element-11844">
                <BarChart3 className="w-5 h-5 text-indigo-600" data-id="element-11845" />
                <h3 className="font-bold text-slate-900" data-id="element-11846">
                  Аналитика кампании: {selectedCampaign.astrologer}
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11848" />} onClick={() => setActiveModal(null)} data-id="element-11847" />
            </div>
            <div className="p-6" data-id="element-11849">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-11850">
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11851">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11852">Показы</div>
                  <div className="text-2xl font-bold text-slate-900" data-id="element-11853">
                    {selectedCampaign.impressions.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11854">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11855">Клики</div>
                  <div className="text-2xl font-bold text-blue-600" data-id="element-11856">
                    {selectedCampaign.clicks.toLocaleString()}
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11857">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11858">CTR</div>
                  <div className="text-2xl font-bold text-indigo-600" data-id="element-11859">
                    {selectedCampaign.impressions > 0 ? (selectedCampaign.clicks / selectedCampaign.impressions * 100).toFixed(2) : '0.00'}
                    %
                  </div>
                </div>
                <div className="bg-slate-50 p-4 rounded-xl border border-slate-100" data-id="element-11860">
                  <div className="text-xs text-slate-500 mb-1" data-id="element-11861">Записи</div>
                  <div className="text-2xl font-bold text-emerald-600" data-id="element-11862">
                    {selectedCampaign.bookings.toLocaleString()}
                  </div>
                </div>
              </div>

              <div className="h-48 bg-slate-50 rounded-xl border border-slate-200 flex items-center justify-center mb-6" data-id="element-11863">
                <div className="text-slate-400 flex items-center gap-2" data-id="element-11864">
                  <BarChart3 className="w-5 h-5" data-id="element-11865" />
                  <span data-id="element-11866">График конверсий (Mock)</span>
                </div>
              </div>

              <div className="flex justify-end" data-id="element-11867">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11868">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}

        {/* A/B TEST MODAL */}
        {activeModal === 'ab_test' && selectedCampaign && <Card className="w-full max-w-2xl shadow-2xl overflow-hidden animate-in zoom-in-95" padding="none" data-id="element-11869">
            <div className="p-4 border-b border-slate-200 bg-slate-50 flex justify-between items-center" data-id="element-11870">
              <div className="flex items-center gap-2" data-id="element-11871">
                <SplitSquareHorizontal className="w-5 h-5 text-indigo-600" data-id="element-11872" />
                <h3 className="font-bold text-slate-900" data-id="element-11873">
                  A/B Тестирование профиля
                </h3>
              </div>
              <Button variant="ghost" size="sm" icon={<X className="w-5 h-5" data-id="element-11875" />} onClick={() => setActiveModal(null)} data-id="element-11874" />
            </div>
            <div className="p-6" data-id="element-11876">
              <p className="text-sm text-slate-600 mb-6" data-id="element-11877">
                Сравнение эффективности различных вариантов карточки для
                кампании <strong data-id="element-11878">{selectedCampaign.id}</strong>.
              </p>

              <div className="space-y-4" data-id="element-11879">
                {selectedCampaign.creatives?.map((cr: any, idx: number) => <div key={cr.id} className={`p-4 rounded-xl border ${idx === 0 ? 'border-emerald-400 bg-emerald-50/30' : 'border-slate-200 bg-white'} flex items-center justify-between`} data-id="element-11880">
                    <div className="flex items-center gap-4" data-id="element-11881">
                      <div className={`w-12 h-12 rounded-lg flex items-center justify-center font-bold text-lg ${idx === 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-600'}`} data-id="element-11882">
                        {idx === 0 ? 'A' : 'B'}
                      </div>
                      <div data-id="element-11883">
                        <div className="font-bold text-slate-900" data-id="element-11884">
                          {cr.name}
                        </div>
                        <div className="text-xs text-slate-500" data-id="element-11885">
                          ID: {cr.id}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-6 text-center" data-id="element-11886">
                      <div data-id="element-11887">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11888">
                          CTR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11889">
                          {cr.ctr}
                        </div>
                      </div>
                      <div data-id="element-11890">
                        <div className="text-[10px] text-slate-500 uppercase" data-id="element-11891">
                          CR
                        </div>
                        <div className={`font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-900'}`} data-id="element-11892">
                          {cr.cr}
                        </div>
                      </div>
                    </div>
                    {idx === 0 && <Badge variant="success" data-id="element-11893">Winner</Badge>}
                  </div>)}
              </div>

              <div className="mt-6 flex justify-end" data-id="element-11894">
                <Button variant="outline" onClick={() => setActiveModal(null)} data-id="element-11895">
                  Закрыть
                </Button>
              </div>
            </div>
          </Card>}
      </div>;
  };
  return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500" data-id="element-11896">
      {renderKpis()}
      {renderAiRecommendations()}
      {renderTable()}
      {renderModals()}
    </div>;
}